import { Link } from "react-router-dom";
import { ArrowUpRight, ExternalLink } from "lucide-react";
import { cleanName } from "./PlayerCard";

const POS_LABEL = { GK: "Goalkeeper", DEF: "Defender", MID: "Midfielder", FWD: "Forward" };

/** Dense editorial row for ranked player lists. */
export const PlayerRow = ({ player, index, showYear }) => (
  <Link
    to={`/hall-of-fame/${player.slug}`}
    data-testid={`player-row-${player.slug}`}
    className="group grid grid-cols-[46px_1fr_auto] items-center gap-4 border-b border-white/10 py-4 transition-colors hover:bg-white/[0.04] md:grid-cols-[64px_minmax(0,2fr)_minmax(0,1.4fr)_minmax(0,1fr)_auto] md:gap-6 md:py-5"
  >
    <span className="mono text-sm text-maasai md:text-base">
      {showYear
        ? player.year || "—"
        : player.rank
        ? String(player.rank).padStart(2, "0")
        : String(index + 1).padStart(2, "0")}
    </span>

    <span className="flex min-w-0 items-center gap-3">
      {player.portrait_url ? (
        <img
          src={player.portrait_url}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="h-10 w-10 shrink-0 border border-white/15 object-cover object-top grayscale transition-all duration-500 group-hover:grayscale-0 sm:h-11 sm:w-11"
        />
      ) : null}
      <span className="min-w-0">
      <span className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
        <span className="truncate font-display text-lg uppercase leading-tight tracking-tight text-white transition-colors group-hover:text-ochre md:text-2xl">
          {cleanName(player.name)}
        </span>
        {player.milestone_tag ? (
          <span
            data-testid={`player-tag-${player.slug}`}
            className="shrink-0 border border-maasai px-2 py-[3px] text-[0.55rem] font-bold uppercase tracking-[0.18em] text-maasai"
          >
            {player.milestone_tag}
          </span>
        ) : null}
      </span>
      <span className="mt-1 block truncate text-[0.62rem] uppercase tracking-[0.18em] text-zinc-500 md:hidden">
        {[player.club, POS_LABEL[player.position] || player.position].filter(Boolean).join(" · ")}
      </span>
      </span>
    </span>

    <span className="hidden min-w-0 truncate text-sm text-zinc-400 md:block">{player.club || "—"}</span>
    <span className="hidden min-w-0 truncate text-[0.68rem] uppercase tracking-[0.16em] text-zinc-500 md:block">
      {POS_LABEL[player.position] || player.position}
    </span>

    <span className="flex items-center gap-3 justify-self-end">
      {player.total_score ? (
        <span className="mono text-sm text-ochre md:text-base">{player.total_score}</span>
      ) : null}
      <ArrowUpRight size={16} className="text-zinc-400 transition-all group-hover:translate-x-0.5 group-hover:text-ochre" />
    </span>
  </Link>
);

/** Dense editorial row for news and football development updates. */
export const StoryRow = ({ story, index, showIndex = true }) => {
  const source = (story.sources || []).find((s) => (typeof s === "string" ? s : s?.url));
  const src = typeof source === "string" ? { url: source, publisher: "Source" } : source;
  const url = src?.url || story.source_url;
  return (
    <div className="border-b border-white/10" data-testid={`story-row-wrap-${story.slug}`}>
      <Link
        to={`/news/${story.slug}`}
        data-testid={`story-row-${story.slug}`}
        className="group grid grid-cols-[1fr_auto] items-start gap-4 py-5 transition-colors hover:bg-white/[0.04] md:grid-cols-[64px_minmax(0,1.1fr)_minmax(0,2.4fr)_auto] md:items-center md:gap-6"
      >
        {showIndex ? (
          <span className="mono hidden text-sm text-maasai md:block">{String(index + 1).padStart(2, "0")}</span>
        ) : (
          <span className="hidden md:block" />
        )}

        <span className="hidden flex-col md:flex">
          <span className="text-[0.62rem] font-bold uppercase tracking-[0.22em] text-ochre">{story.category}</span>
          <span className="mono mt-2 text-xs text-zinc-500">{story.date}</span>
        </span>

        <span className="min-w-0">
          <span className="mb-2 flex items-center gap-3 md:hidden">
            <span className="text-[0.6rem] font-bold uppercase tracking-[0.22em] text-ochre">{story.category}</span>
            <span className="mono text-[0.62rem] text-zinc-500">{story.date}</span>
          </span>
          <span className="block font-display text-lg uppercase leading-tight tracking-tight text-white transition-colors group-hover:text-ochre md:text-2xl">
            {story.title}
          </span>
          <span className="mt-2 line-clamp-2 block text-sm leading-relaxed text-zinc-400">{story.excerpt}</span>
        </span>

        <ArrowUpRight
          size={18}
          className="mt-1 shrink-0 justify-self-end text-zinc-400 transition-all group-hover:translate-x-0.5 group-hover:text-ochre md:mt-0"
        />
      </Link>
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          data-testid={`story-row-source-${story.slug}`}
          className="mb-4 inline-flex items-center gap-2 text-[0.58rem] font-bold uppercase tracking-[0.16em] text-zinc-500 transition-colors hover:text-ochre md:ml-[64px]"
        >
          Read at source: {src?.publisher || "external"} <ExternalLink size={11} />
        </a>
      ) : null}
    </div>
  );
};

export const ListHeader = ({ columns }) => (
  <div className="hidden grid-cols-[64px_minmax(0,2fr)_minmax(0,1.4fr)_minmax(0,1fr)_auto] gap-6 border-b border-white/20 pb-3 md:grid">
    {columns.map((c) => (
      <span key={c} className="text-[0.58rem] font-bold uppercase tracking-[0.24em] text-zinc-400">
        {c}
      </span>
    ))}
  </div>
);
